import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Box, Slide } from "@mui/material";
import { useFetchUserList } from "../redux/hooks";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { SIZE, COLOR, Button } from "@metacrm/metacrm-material-ui/dist/Button";
import { useDispatch } from "react-redux";
import {
  alertServerError,
  formatWalletAgeDisplay,
} from "features/helpers/utils";
import { StyledFilterBtn } from "./CustomerList.styles";
import TableMenu from "components/CustomTable/TableMenu";
import axios from "axios";
import { apiUrl } from "features/configure";
import ExportModal from "../components/ImportList/ExportModal";
import { InputField } from "@metacrm/metacrm-material-ui/dist/InputField";
import _, { omit } from "lodash";
import AssignTagsModal from "../components/ImportList/AssignTagsModal";
import UnassignTagsModal from "../components/ImportList/UnassignTagsModal";
import useGetPagePermission from "hooks/useGetPagePermission";
import { useSelector } from "react-redux";
import { selectRemindBarShow } from "../redux/globalState/globalState.selector";
import UserOverview from "../ticket/UserOverview";
import CRMTable from "@metacrm/metacrm-material-ui/dist/Table";
import LoadingIcon from "components/LoadingComponent/LoadingIcon";
import useGetCDPTable from "./hooks/useGetCDPTable";
import FilterModal from "./components/FilterModal";
import { isObject } from "lodash";
import TokensModal from "./components/TokensModal";
import EmptyIconStatus from "components/EmptyState/EmptyIconStatus";
import FilterList from "./components/FilterList";
import useNavigateEntity from "hooks/useNavigateEntity";
import ImportUserModal from "./components/ImportUserModal";
import { useIntl } from "react-intl";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import { useSnackbar } from "features/common/redux/snackbar";

const sortMapping = {
  cexLabels: "fundedBy",
  tokens: "totalTokenCount",
  activeChains: "chainIdsCount",
  walletAge: "walletAgeInMonths",
  source: "source.type",
  lastEngagement: "lastEngagement.date",
};

const CustomerList = () => {
  const { formatMessage } = useIntl();
  const [entityName] = useOutletContext();
  const confirm = useConfirm();
  const remindBarShow = useSelector(selectRemindBarShow);
  const [openModal, setOpenModal] = useState("");
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedRowUserData, setSelectedRowUserData] = useState([]);
  const [open, setOpen] = useState(false);
  let [searchParams, setSearchParams] = useSearchParams();
  const openUserDetail = useMemo(() => {
    return searchParams.get("uid");
  }, [searchParams]);

  const {
    userListFilter: filter,
    userList,
    userListCount,
    importStatus,
    importCount,
    userSource,
    updateUserListFilter,
    fetchUserSource,
    fetchUserAdminList,
  } = useFetchUserList();
  const { navigateEntity } = useNavigateEntity();

  const lastFetchTime = useRef(new Date());
  const pollingIntervalRef = useRef(null);
  const previousImportStatusRef = useRef(null);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const fromSource = searchParams.get("fromSource");

  const { page, orderData, search } = filter;

  const [tokens, setTokens] = useState(null);

  const { headers, renderContents } = useGetCDPTable(setTokens);

  const selectedCount = selectedRowUserData.length;

  const handleOpenImportPopover = (e) => {
    setOpen(true);
  };

  const handleCloseImportPopover = () => {
    setOpen(false);
  };

  const hasFetchedFromSource = useRef(false);

  useEffect(() => {
    if (fromSource === "websiteTraffic" && !hasFetchedFromSource.current) {
      hasFetchedFromSource.current = true;
      updateUserListFilter({
        ...filter,
        orderData: { sort: "lastEngagement", order: -1 },
      });
      getUserList();
    } else {
      handleFetchUserSource();
    }
  }, [fromSource]);

  useEffect(() => {
    if (!hasFetchedFromSource.current) {
      getUserList();
    }
  }, [filter]);

  const handleFetchUserSource = () => {
    fetchUserSource({ entityName });
  };

  const abortControllerRef = useRef(new AbortController());

  const getUserList = async (isPolling = false) => {
    if (!isPolling) {
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
    }

    try {
      if (isInitialLoad || (isPolling && userListCount === 0)) {
        setIsLoading(true);
      }

      const postFilter = {
        ...filter,
        order: filter?.orderData?.order,
        sortBy: sortMapping[filter?.orderData?.sort] || filter?.orderData?.sort,
        source: filter?.source?.value,
        lastEngagement: filter?.lastEngagement?.every((item) => item !== null)
          ? {
              from: filter?.lastEngagement[0],
              to: filter?.lastEngagement[1],
            }
          : null,
        lastEngagementType: filter?.lastEngagementType?.value,
        cexLabels: filter?.cexLabels?.map((label) =>
          isObject(label) ? label.value : label
        ),
        tags: filter?.tags?.map((tag) => (isObject(tag) ? tag._id : tag)),
        ecosystems: filter?.ecosystems?.map((ecosystem) =>
          isObject(ecosystem) ? ecosystem.value : ecosystem
        ),
        chainIds: filter?.chainIds?.map((chainId) =>
          isObject(chainId) ? chainId.value : chainId
        ),
        assets: filter?.assets
          ?.filter((item) => item.chainId)
          ?.map((asset) => ({
            chainId: asset.chainId.value,
            assetType: asset.assetType.value,
            token: {
              address: asset.token.value,
            },
            included: true,
          })),
        walletTotalValue: filter?.walletTotalValue && {
          from:
            filter?.walletTotalValue[0] !== 0
              ? filter?.walletTotalValue[0]
              : null,
          to:
            filter?.walletTotalValue[1] !== 50000
              ? filter?.walletTotalValue[1]
              : null,
        },
        walletAge: filter?.walletAge && {
          from: formatWalletAgeDisplay(filter?.walletAge[0]),
          to: formatWalletAgeDisplay(filter?.walletAge[1]),
        },
      };

      await fetchUserAdminList({
        entityName,
        filter: postFilter,
        signal: !isPolling ? abortControllerRef.current.signal : undefined,
      });

      lastFetchTime.current = new Date();

      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("Request was canceled");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(() => {
      getUserList(true);
    }, 5000);
  }, [entityName, userListCount, isInitialLoad, filter]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    const shouldPoll = userListCount < 100 && importStatus === "processing";

    if (shouldPoll) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [userListCount, importStatus, startPolling, stopPolling]);

  const handleRequestSort = (event, property) => {
    const { sort, order } = orderData;
    const isAsc = sort === property && order === 1;
    updateUserListFilter({
      ...filter,
      page: 1,
      orderData: {
        sort: property,
        order: isAsc ? -1 : 1,
      },
    });
  };

  const selectedUserData = selectedRowUserData;

  const deleteSomeUsers = async () => {
    try {
      if (selectedCount == 0) return;
      const selectedUids = _.map(selectedRowUserData, "_id");

      await axios.post(`${apiUrl}/api/userSource/${entityName}/deleteUsers`, {
        uids: selectedUids,
      });

      setSelectedRowUserData([]);
      handleFetchUserSource();
      getUserList();
    } catch (err) {
      dispatch(alertServerError(err));
    }
  };

  const handleDeleteSomeUsers = () => {
    confirm({
      title: formatMessage({
        id: "popup.deleteUser.title",
      }),
      content: formatMessage({
        id: "popup.deleteUser.message",
      }),
      confirmText: formatMessage({
        id: "global.btn.delete",
      }),
      cancelText: formatMessage({
        id: "global.btn.cancel",
      }),
    })
      .then(() => {
        deleteSomeUsers();
      })
      .catch(() => {});
  };

  const pagePermission = useGetPagePermission();
  const { canEdit } = pagePermission("customerList");

  const selectedMenu = canEdit
    ? [
        {
          children: (
            <Box sx={{ color: "#fff" }} className="item-flex">
              <i
                className="meta-crm-icon-ic_cancel font-size-16"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setOpenModal("");
                  setSelectedRowUserData([]);
                }}
              />

              <Box ml="16px">{selectedCount} Selected</Box>
            </Box>
          ),
        },
        {
          children: (
            <Box
              sx={{ height: "29px", width: "1px", backgroundColor: "#FFF" }}
            />
          ),
        },

        {
          children: (
            <Box
              sx={{ cursor: "pointer", color: "#fff", position: "relative" }}
              onClick={() => {
                // setOpenAssignTags("selected");
                // setOpenUnassignTags("selected");
                if (openModal === "tags") {
                  setOpenModal("");
                } else {
                  setOpenModal("tags");
                }
              }}
            >
              <Box className="item-flex">
                {" "}
                {formatMessage({ id: "global.commonWord.tags" })}
                <i
                  className="meta-crm-icon-ic_arrow-down font-size-16"
                  style={{ marginLeft: "4px" }}
                />
              </Box>

              {openModal === "tags" && (
                <Box
                  sx={{
                    position: "absolute",
                    bgcolor: "#2E2E2E",
                    color: "#fff",
                    fontSize: "12px",
                    boxShadow: "0px 0px 20px 0px rgba(0, 0, 0, 0.05)",
                    padding: "16px 24px",
                    lineHeight: "20px",
                    top: "-115px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    borderRadius: "4px",
                  }}
                >
                  <Box
                    mb="16px"
                    sx={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenModal("unassignTag");
                    }}
                  >
                    {formatMessage({ id: "global.btn.unassignTags" })}
                  </Box>
                  <Box
                    sx={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenModal("assignTag");
                    }}
                  >
                    {formatMessage({ id: "global.btn.assignTags" })}
                  </Box>
                </Box>
              )}
            </Box>
          ),
        },
        {
          children: (
            <Box
              sx={{ cursor: "pointer", color: "#fff" }}
              onClick={(e) => {
                e.stopPropagation();
                if (selectedRowUserData.length > 0) {
                  updateUserListFilter({
                    ...filter,
                    selectedUser: selectedRowUserData?.map((user) => user._id),
                  });
                } else {
                  updateUserListFilter(omit(filter, "selectedUser"));
                }
                navigateEntity("/customers/analysis");
              }}
              id="btn_viewCustomerReport"
            >
              {formatMessage({ id: "global.function.viewReport" })}
            </Box>
          ),
        },
        {
          children: (
            <Box
              sx={{ cursor: "pointer", color: "#fff" }}
              onClick={() => {
                setOpenModal("export");
              }}
            >
              {formatMessage({ id: "global.btn.export" })}
            </Box>
          ),
        },
        // {
        //   children: (
        //     <Box
        //       sx={{ cursor: "pointer", color: "#FC5555" }}
        //       onClick={handleDeleteSomeUsers}
        //     >
        //       {formatMessage({ id: "global.btn.delete" })}
        //     </Box>
        //   ),
        // },
      ]
    : [
        {
          children: (
            <Box sx={{ color: "#fff" }}>
              {formatMessage(
                { id: "global.commonWord.selected" },
                { count: selectedCount }
              )}
            </Box>
          ),
        },
        {
          children: (
            <Box
              sx={{ cursor: "pointer", color: "#fff" }}
              onClick={() => {
                setOpenModal("export");
              }}
            >
              {formatMessage({ id: "global.btn.export" })}
            </Box>
          ),
        },
      ];

  const getUsersIntersectionTag = () => {
    const data = _.intersectionBy(userList, selectedRowUserData, "_id");
    const intersectionTags = _.intersectionBy(..._.map(data, "tags"), "_id");
    return intersectionTags;
  };

  const hasFilter = useMemo(() => {
    const filterKeys = Object.keys(filter);
    return filterKeys.some((key) => {
      if (
        key === "page" ||
        key === "orderData" ||
        key === "search" ||
        key === "selectedUser"
      ) {
        return false;
      }

      const value = filter[key];

      // Check if value is empty array
      if (Array.isArray(value) && value.length === 0) {
        return false;
      }

      // Check if value is empty/null/undefined
      if (!value) {
        return false;
      }

      return true;
    });
  }, [filter]);
  const minusHeight = 203 + (remindBarShow ? 26 : 0) + (hasFilter ? 57 : 0);

  useEffect(() => {
    if (
      importStatus === "processing" &&
      previousImportStatusRef.current !== "processing"
    ) {
      enqueueSnackbar({
        message: formatMessage({ id: "global.snackbar.importing" }),
        options: {
          variant: "success",
        },
        action: (
          <Box
            onClick={() => {
              closeSnackbar();
            }}
            sx={{
              color: "#fff",
              fontSize: "14px",
              fontWeight: 600,
              padding: "0px 8px",
              color: "#CBE6FF",
              cursor: "pointer",
            }}
          >
            {formatMessage({ id: "global.btn.gotIt" })}
          </Box>
        ),
        autoHideDuration: null,
      });
    }

    if (
      previousImportStatusRef.current === "processing" &&
      importStatus !== "processing"
    ) {
      console.log("closeSnackbar");
      closeSnackbar();

      if (importStatus === "done") {
        // 延遲一點時間確保前一個 snackbar 已經關閉
        setTimeout(() => {
          enqueueSnackbar({
            message: formatMessage({ id: "global.snackbar.success.import" }),
            options: {
              variant: "success",
            },
          });
        }, 100);
      }
    }

    previousImportStatusRef.current = importStatus;
  }, [importStatus, enqueueSnackbar]);

  useEffect(() => {
    return () => {
      closeSnackbar();
    };
  }, [closeSnackbar]);

  return (
    <Box>
      <Slide direction="left" in={!_.isEmpty(openUserDetail)}>
        <Box
          position={"absolute"}
          display={!_.isEmpty(openUserDetail) ? "block" : "none"}
          top="0px"
          bottom="0px"
          left="0px"
          right="0px"
          p="8px"
          zIndex={500}
          backgroundColor="#FAFAFD"
        >
          <UserOverview
            uid={_.isEmpty(openUserDetail) ? "" : [openUserDetail]}
            getUserList={getUserList}
          />
        </Box>
      </Slide>
      <Box
        display="flex"
        alignItems="flex-end"
        justifyContent="space-between"
        mb="24px"
        className="fade-up"
      >
        <div>
          <div className="title">
            {formatMessage({ id: "cdp.header.title" })}
          </div>
          <div className="description">
            {formatMessage({ id: "cdp.header.description" })}
          </div>
        </div>
        {canEdit && (
          <Button color={COLOR.SECONDARY} onClick={handleOpenImportPopover}>
            {formatMessage({ id: "cdp.btn.import" })}
          </Button>
        )}
      </Box>
      {canEdit && open && (
        <ImportUserModal
          open={true}
          handleClose={handleCloseImportPopover}
          getUserList={getUserList}
        />
      )}

      <div className="fade-up-more">
        <Box
          display="flex"
          alignItems="flex-end"
          mb="8px"
          justifyContent="space-between"
        >
          <Box className="item-flex">
            <div style={{ marginRight: 8 }}>
              <InputField
                size={SIZE.MEDIUM}
                onEnter={(v, e) => e.target.blur()}
                placeholder={formatMessage({ id: "cdp.search.placeholder" })}
                sx={{ width: "360px" }}
                startAdornment={
                  <i
                    className="meta-crm-icon-ic_search font-size-16"
                    style={{ marginRight: 5, color: "#A5A5A5" }}
                  />
                }
                value={search}
                onChange={(e) =>
                  updateUserListFilter({
                    ...filter,
                    search: e.target.value,
                    page: 1,
                  })
                }
                noBorder
                onBlur={() => {
                  setSelectedRowUserData([]);
                }}
              />
            </div>
            <StyledFilterBtn
              onClick={() => {
                setOpenModal("filter");
                setSelectedRowUserData([]);
              }}
            >
              <i className="meta-crm-icon-ic_filter2 font-size-14" />
              <Box ml="4px">{formatMessage({ id: "global.btn.filter" })}</Box>
            </StyledFilterBtn>
          </Box>
          <Box fontSize={12} color="#222" fontWeight={400}>
            {importStatus === "processing" ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <img
                  src={require("assets/img/import-loading.gif")}
                  alt="loading"
                  style={{ width: 12, height: 12, marginRight: 8 }}
                />
                {formatMessage(
                  { id: "global.import.customer" },
                  { count: importCount || 0 }
                )}
              </Box>
            ) : (
              `${userListCount} ${formatMessage({
                id: "global.unit.customer",
              })}`
            )}
          </Box>
        </Box>

        <FilterList
          hasFilter={hasFilter}
          selectedRowUserData={selectedRowUserData}
          openFilterModal={() => setOpenModal("filter")}
        />
        <div style={{ position: "relative" }}>
          <CRMTable
            checkbox
            containerStyle={{
              maxHeight: `calc(100vh - ${minusHeight}px)`,
            }}
            rowData={userList}
            loadingIndicatorAmount={25}
            selectedRowData={selectedRowUserData}
            setSelectedRowData={setSelectedRowUserData}
            noDataTitle="No User"
            onRequestSort={handleRequestSort}
            orderBy={orderData.sort}
            order={_.get(orderData, "order") === 1 ? "asc" : "desc"}
            headers={headers}
            contents={_.map(userList, (user, i) => {
              return renderContents(user);
            })}
            isLoading={isLoading}
            LoadingContent={<LoadingIcon />}
            noDataComponent={
              <EmptyIconStatus
                icon="table"
                wording="later"
                emptyTitle={formatMessage({
                  id: "emptyState.table.title",
                })}
                emptyDescription={formatMessage({
                  id: "emptyState.table.description",
                })}
              />
            }
            onRowClick={(uid, rowData) => {
              setSearchParams({ uid: rowData._id });
            }}
            page={page}
            setPage={(page) => updateUserListFilter({ ...filter, page })}
            pageText={formatMessage({ id: "global.pagination.page" })}
            pageOfText={formatMessage({ id: "global.pagination.of" })}
            dataCounts={userListCount}
            leftFooter={
              // <Box fontSize={12} className="item-flex">
              //   <Box color="#555">
              //     Last Update Time:{" "}
              //     {formatDate(lastFetchTime.current, { time: true })}
              //   </Box>
              //   <Box
              //     color="#7B61FF"
              //     sx={{
              //       textDecoration: "underline",
              //       cursor: "pointer",
              //       ml: "8px",
              //     }}
              //     onClick={getUserList}
              //   >
              //     Refresh
              //   </Box>
              // </Box>
              <></>
            }
          />
        </div>
      </div>
      <TableMenu
        absolute
        show={selectedCount > 0}
        tabs={selectedMenu}
        style={{ zIndex: 100 }}
      />

      {/* modals */}
      <ExportModal
        open={openModal === "export"}
        onClose={() => setOpenModal("")}
        dataset={_.find(userSource, { _id: "all" })}
        entityName={entityName}
        choose={_.map(selectedUserData, "_id")}
      />
      <FilterModal
        open={openModal === "filter"}
        handleClose={() => setOpenModal("")}
      />
      {tokens && (
        <TokensModal
          open={true}
          handleClose={() => setTokens(null)}
          tokens={tokens}
        />
      )}
      {/* {canEdit && ( */}
      <>
        <AssignTagsModal
          open={openModal === "assignTag"}
          onClose={() => {
            setOpenModal("");
          }}
          entityName={entityName}
          choose={_.map(selectedUserData, "_id")}
        />
        <UnassignTagsModal
          open={openModal === "unassignTag"}
          onClose={() => {
            setOpenModal("");
          }}
          entityName={entityName}
          choose={_.map(selectedUserData, "_id")}
          uniqTags={getUsersIntersectionTag()}
        />
      </>
      {/* )} */}
    </Box>
  );
};

export default CustomerList;
